import flattenDeep from 'lodash/flattenDeep'
import some from 'lodash/some'
import every from 'lodash/every'
import orderBy from 'lodash/orderBy'
import map from 'lodash/map'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'

import transformJson from '@/utils/transformJson'

import { server } from '@/services/'

const state = {
  isFetching: false,
  isPosting: false,
  existingAssignments: [],
  assignments: [],
  stations: [],
  zones: [],
  carInStations: [],
  cars: [],
  joinedData: [],
  cleaners: []
}

const mutations = {
  setIsPosting (state, payload) {
    state.isPosting = payload
  },
  setIsFetching (state, payload) {
    state.isFetching = payload
  },
  setAssignments (state, payload) {
    state.assignments = payload
  },
  setExistingAssignments (state, payload) {
    state.existingAssignments = payload
  },
  setZones (state, payload) {
    state.zones = payload
  },
  setStations (state, payload) {
    state.stations = payload
  },
  setCars (state, payload) {
    state.cars = payload
  },
  setCarInStations (state, payload) {
    state.carInStations = payload
  },
  setJoinedData (state, payload) {
    state.joinedData = payload
  },
  setCleaners (state, payload) {
    state.cleaners = payload
  },
  toggleZone (state, payload) {
    state.joinedData[payload.idx].COLLAPSED = !state.joinedData[payload.idx].COLLAPSED
    state.joinedData[payload.idx].SHOW_STATION = !state.joinedData[payload.idx].SHOW_STATION
  },
  toggleStation (state, payload) {
    let station = findStation(state, payload.stationCode)
    station.COLLAPSED = !station.COLLAPSED
    // re-assign updated data to joinedData
    // state.joinedData.map(zone => ({...zone, STATIONS: stations.filter(station => station.ZONE_CODE === zone.CODE)}))
  },
  stationSelected (state, payload) {
    let zoneIdx = state.joinedData.findIndex(zone => zone.CODE === payload.ZONE_CODE)
    let stationIdx = state.joinedData[zoneIdx].STATIONS.findIndex(station => station.CODE === payload.CODE)
    state.joinedData[zoneIdx].STATIONS[stationIdx].SELECTED = !state.joinedData[zoneIdx].STATIONS[stationIdx].SELECTED
    if (!state.joinedData[zoneIdx].STATIONS[stationIdx].SELECTED) {
      state.joinedData[zoneIdx].STATIONS[stationIdx].ASSIGNED_CLEANER = null
    }
  },
  bulkChanged (state, payload) {
    let idx = state.joinedData.findIndex(zone => zone.CODE === payload.CODE)
    state.joinedData[idx].IS_BULK = !state.joinedData[idx].IS_BULK
    if (!state.joinedData[idx].IS_BULK) {
      state.joinedData[idx].ASSIGNED_CLEANER = null
    }
    if (state.joinedData[idx].IS_BULK) {
      state.joinedData[idx].STATIONS.map(station => {
        station.SELECTED = true
        return station
      })
    } else {
      state.joinedData[idx].STATIONS.map(station => {
        station.SELECTED = false
        station.ASSIGNED_CLEANER = null
        return station
      })
    }
  },
  setBulkCleaner (state, payload) {
    let idx = state.joinedData.findIndex(zone => zone.CODE === payload.zoneCode)
    state.joinedData[idx].ASSIGNED_CLEANER = payload.cleaner
    state.joinedData[idx].STATIONS.map(station => {
      station.ASSIGNED_CLEANER = payload.cleaner
      return station
    })
  },
  setStationCleaner (state, payload) {
    let station = findStation(state, payload.stationCode)
    station.ASSIGNED_CLEANER = payload.cleaner
  },
  setAssignmentStatus (state, payload) {
    let station = findStation(state, payload.stationCode)
    station.ASSIGNMENT_STATUS = payload.status ? 1 : 0
    // re-assign updated data to joinedData
    // updateStations(state, stations)
  },
  carNoteUpdated (state, payload) {
    let station = findStation(state, payload.stationCode)
    let cist = station.CIST_LIST.find(cist => cist.CODE === payload.cistCode)
    cist.ASSIGNMENT.NOTE = payload.note
  }
}

/**
 * Find station to be updated.
 * @param  {Object} state
 * @param  {Object} station
 * @return {Void}
 */
function findStation(state, stationCode) {
  let stations = state.joinedData.map(zone => zone.STATIONS)
  stations = flattenDeep(stations)
  let station = stations.find(station => station.CODE === stationCode)
  return station
}

const actions = {
  fetchAssignments ({commit}) {
    return new Promise((resolve, reject) => {
      server.get('/ops/getCleanerAssignments').then(
        resp => {
          // transform SNAKE_CASE to camelCase
          let normalizedKeys = transformJson(resp.data.msgContent.data, true)
          let arr = map(normalizedKeys, (o) => { return omit(o, 'userLog', 'code') })
          // this modified assignments will be used later as comparer upon submitAssignment
          commit('setExistingAssignments', arr)
          commit('setAssignments', resp.data.msgContent.data)
          resolve()
        },
        err => reject(err)
      )
    })
  },
  fetchZones ({commit}) {
    return new Promise((resolve, reject) => {
      server.post('/proxy/cocos/getZones').then(
        resp => {
          let zones = resp.data.msgContent.data.map(zone => ({...zone, COLLAPSED: false}))
          commit('setZones', zones)
          resolve()
        },
        err => reject(err)
      )
    })
  },
  fetchStations ({commit}) {
    return new Promise((resolve, reject) => {
      server.post('/proxy/cocos/getStations').then(
        resp => {
          let stations = resp.data.msgContent.data.map(station => ({...station, COLLAPSED: false}))
          commit('setStations', stations)
          resolve()
        },
        err => reject(err)
      )
    })
  },
  fetchCars ({commit}) {
    return new Promise((resolve, reject) => {
      server.post('/proxy/cocos/getCars', { ACTIVE: true }).then(
        resp => {
          commit('setCars', resp.data.msgContent.data)
          resolve()
        },
        err => reject(err)
      )
    })
  },
  fetchCarInStations ({commit}) {
    return new Promise((resolve, reject) => {
      server.post('/proxy/cocos/getCarInStations', { ACTIVE: true }).then(
        resp => {
          commit('setCarInStations', resp.data.msgContent.data)
          resolve()
        },
        err => reject(err)
      )
    })
  },
  fetchCleaners({commit}) {
    return new Promise((resolve, reject) => {
      server.get('/sso/getUA_Cleaners').then(
        resp => {
          commit('setCleaners', resp.data.msgContent.data)
          resolve()
        },
        err => reject(err)
      )
    })
  },
  /**
   * Compose data to match assignment UI.
   * Stations state need assignment data,
   * to achieve it FE must join the data provided by BE.
   * Also we have to assign new property into some states for UI needed.
   *
   * @param  {Function} commit
   * @param  {Object} state
   * @return {Void}
   */
  composeData ({commit, state}) {
    // assign car & assignment into cist
    let cistWithCarAssignment = state.carInStations.map(cist => ({
      ...cist,
      CAR_DETAIL: state.cars.find(car => car.CODE === cist.CAR_CODE) || {},
      ASSIGNMENT: some(state.assignments, {'CAR_IN_STATION_CODE': cist.CODE})
        ? state.assignments.find(assignment => assignment.CAR_IN_STATION_CODE === cist.CODE) : { NOTE: '' }
    }))
    // assign cist into station
    let stationWithCist = state.stations.map(station => ({
      ...station,
      CIST_LIST: cistWithCarAssignment.filter(cist => cist.STATION_CODE === station.CODE)
    }))
    // assign cleaner into station
    stationWithCist = stationWithCist.map(station => ({
      ...station,
      ASSIGNED_CLEANER: station.CIST_LIST.length && station.CIST_LIST[0].ASSIGNMENT.hasOwnProperty('USER_ACCOUNT_CODE')
        ? station.CIST_LIST[0].ASSIGNMENT.USER_ACCOUNT_CODE : null
    }))
    // assign status of assignment into station
    stationWithCist = stationWithCist.map(station => ({
      ...station,
      ASSIGNMENT_STATUS: station.CIST_LIST.length && station.CIST_LIST[0].ASSIGNMENT.hasOwnProperty('STATUS') ? station.CIST_LIST[0].ASSIGNMENT.STATUS : 0
    }))
    // determine selected prop on station depend on assigned_cleaner value
    stationWithCist = stationWithCist.map(station => ({...station, SELECTED: station.ASSIGNED_CLEANER ? true  : false}))
    // assign composed stations into zone
    let zoneWithStation = state.zones.map(zone => ({...zone, STATIONS: orderBy(stationWithCist.filter(station => station.ZONE_CODE === zone.CODE), 'NAME')}))
    // determine bulk assignment for zone
    zoneWithStation = zoneWithStation.map(zone => {
      let stations = zone.STATIONS
      let isAllSelected = every(stations, station => station.SELECTED === true)
      if (isAllSelected && stations.length) {
        zone = {...zone, IS_BULK: true, ASSIGNED_CLEANER: stations.find(station => station.SELECTED).ASSIGNED_CLEANER}
      } else {
        zone = {...zone, IS_BULK: false, ASSIGNED_CLEANER: null}
      }
      return zone
    })
    commit('setJoinedData', zoneWithStation)
  },
  submitAssignment ({ state }) {
    return new Promise((resolve, reject) => {
      let zones = [...state.joinedData]
      let stations = zones.map(zone => zone.STATIONS)
      stations = flattenDeep(stations)
      stations = stations.filter(station => station.SELECTED)

      if (!stations.length) {
        reject({formInvalid: true, msg: 'No station selected'})
      } else if(some(stations, {'ASSIGNED_CLEANER': undefined})) {
        reject({formInvalid: true, msg: 'Selected station must have assigned cleaner'})
      } else {
        let payload = []
        stations.forEach(station => {
          if (station.CIST_LIST.length) {
            station.CIST_LIST.forEach(cist => {
              let tmp = {}
              tmp.userAccountCode = station.ASSIGNED_CLEANER
              tmp.carInStationCode = cist.CODE
              tmp.status = station.ASSIGNMENT_STATUS
              tmp.note =  cist.ASSIGNMENT.NOTE
              if (tmp.status === undefined) {
                tmp.status = 0
              }
              payload.push(tmp)
            })
          }
        })
        payload = payload.filter(comparer(state.existingAssignments))
        server.post('/ops/upsertCleanerAssignments', payload).then(
          resp => resolve(resp),
          err => reject(err)
        )
      }
    })
  }
}

function comparer(otherArray){
  return (current) => {
    return otherArray.filter((other) => {
      return isEqual(current, other)
    }).length == 0;
  }
}

const getters = {

}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
